import { memo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetRoomInfoMutation } from 'app/services/room.service'

import Header from './Header'
import General from './General'
import Files from 'components/file/files/Files'
import Members from 'components/members/Members'
import CustomModal from 'components/modals/CustomModal'
import { TABS } from 'locales/locales'
import { RoomTypes } from 'types/rooms.types'
import { useLazyGetUserGroupsAndChannelsWithMembersInfoQuery } from 'app/services/group.service'
import { GroupPanel } from 'components/group/GroupPanel'
import { UserStatus } from 'types/user.types'
import { useAppSelector } from 'app/hooks'


interface IConversationInfoProps {
  roomId: string
  type: string
  roomName?: string
  isMyRoom: boolean
  onClose: () => void
}


interface Group {
  name: string;
  imageUrl?: string;
  backgroundColor?: string;
  members?: Member[];
}

interface Member {
  userId: string;
  fullname: string;
  status?: UserStatus;  
  imageUrl?: string;
}const ConversationInfo = ({
  isMyRoom,
  roomId,
  type,
  roomName,
  onClose,
}: IConversationInfoProps) => {
  const { t } = useTranslation()
  const [getRoomInfo, { data }] = useGetRoomInfoMutation()
  const [isLoading, setIsLoading] = useState(true)
  const [groups, setGroups] = useState<Group[]>([]);
  const [triggerGetGroups, { data: groupData }] = useLazyGetUserGroupsAndChannelsWithMembersInfoQuery();
  const groupMembersEnabled = useAppSelector(state => state.system.data.features?.isGroupMembersEnabled);
  
  const fetchRoomInfo = async () => {
    try {
      await getRoomInfo({ roomId })
    } catch (e) {
      onClose()
    } finally {
      setIsLoading(false)
    }
  }
  useEffect(() => {
    const fetchInitialData = async () => {
      await fetchRoomInfo(); 
    };
  
    fetchInitialData();
  }, [roomId]);

  useEffect(() => {
    if (data?.general?._id && typeof data?.general?._id === 'string' && groupMembersEnabled) {
      triggerGetGroups(data.general._id);
    }
  }, [isMyRoom, data?.general?._id, triggerGetGroups, groupMembersEnabled]);

  useEffect(() => {
    if (groupData && groupData.success) {
      setGroups(groupData.data);
    }
  }, [groupData]);

  const tab = [{ title: t(TABS.GENERAL) }];
  switch (type) {
    case RoomTypes.GM:
      tab.push({ title: t(TABS.MEMBERS) }, { title: t(TABS.FILES) });
      break;
    case RoomTypes.OTR:
      tab.push({ title: t(TABS.MEMBERS) });
      break;
    default:
      tab.push({ title: t(TABS.FILES) });
      break;
  }
  
  if (groupMembersEnabled && type === RoomTypes.DM ) {
    tab.push({ title: t(TABS.USERS_GROUPS) });
  }

  return (
    <CustomModal
      header={
        <Header type={type} {...data?.general} groupName={roomName} />
      }
      closeModal={onClose}
      tab={tab}
      showButtons={false}
      loading={isLoading}
      hasMember
    >
      <General
        {...data?.general}
        type={type}
        roomId={roomId}
        isMyRoom={isMyRoom}
        onClose={onClose}
      />
      {(type === RoomTypes.GM || type === RoomTypes.OTR) && (
        <Members data={data?.general?.members} />
      )}
      <Files data={data?.files} />
      {groupMembersEnabled && <GroupPanel groups={groups} userId={data?.general?._id} />}
    </CustomModal>
  )
}

export default memo(ConversationInfo);
